import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { fetchProducts } from "../../../api/product";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { DocumentImage } from "../../dashboard/documents/utils";
import { productTypeOptions } from "../../dashboard/products/product-data";

/**
 * Modern Product Selector Component
 * 
 * Features:
 * - Fast product fetching with debounced search
 * - Filter inactive and expired products
 * - Category filtering
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedProducts - Array of selected product objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of products)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: true)
 * @param {Array} props.filters - Additional filters to apply (API filter objects)
 * @param {boolean} [props.defaultSystemProductsOnly] - When true, "System" filter is on when the dialog opens (hasSpareParts)
 * @param {boolean} [props.defaultShowInactive] - Initial "Vis inaktive" when dialog opens
 * @param {boolean} [props.defaultShowExpired] - Initial "Vis utgåtte" when dialog opens
 */
export default function ModernProductSelector({
  open = false,
  selectedProducts = [],
  onChange,
  onClose,
  multiple = true,
  filters = [],
  showHeader = true,
  defaultSystemProductsOnly = false,
  defaultShowInactive = false,
  defaultShowExpired = false,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(defaultShowInactive);
  const [showExpired, setShowExpired] = useState(defaultShowExpired);
  const [systemProductsOnly, setSystemProductsOnly] = useState(defaultSystemProductsOnly);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedIds, setSelectedIds] = useState(
    new Set(selectedProducts.map((p) => p.id))
  );
  const [productQuantities, setProductQuantities] = useState(
    new Map(selectedProducts.map((p) => [p.id, p.quantity || 1]))
  );

  const perPage = 20;

  // Update selectedIds and quantities when selectedProducts prop changes
  useEffect(() => {
    if (open) {
      setSelectedIds(new Set(selectedProducts.map((p) => p.id)));
      setProductQuantities(
        new Map(selectedProducts.map((p) => [p.id, p.quantity || 1]))
      );
    }
  }, [selectedProducts, open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setShowInactive(defaultShowInactive);
      setShowExpired(defaultShowExpired);
      setSystemProductsOnly(defaultSystemProductsOnly);
      setSelectedCategory(null);
      setShowFilters(
        defaultSystemProductsOnly || defaultShowInactive || defaultShowExpired
      );
    } else {
      setShowInactive(defaultShowInactive);
      setShowExpired(defaultShowExpired);
      setSystemProductsOnly(defaultSystemProductsOnly);
      setSelectedCategory(null);
    }
  }, [
    open,
    defaultSystemProductsOnly,
    defaultShowInactive,
    defaultShowExpired,
  ]);

  const filtersSignature = JSON.stringify(Array.isArray(filters) ? filters : []);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query, page) => {
        setIsLoading(true);
        setError(null);

        try {
          const baseFilters = JSON.parse(filtersSignature);
          const mergedFilters = Array.isArray(baseFilters) ? [...baseFilters] : [];
          if (systemProductsOnly) {
            mergedFilters.push({ name: "hasSpareParts", value: true });
          }

          const params = {
            query: query || undefined,
            page: page,
            per_page: perPage,
            sortBy: "id",
            sort: "desc",
            show_inactive: showInactive ? true : false,
            show_expired: showExpired,
            product_type: selectedCategory || undefined,
            fields:
              "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price,status,product_type,main_picture",
            filters: mergedFilters.length > 0 ? mergedFilters : undefined,
          };

          const response = await fetchProducts(params);

          if (response && response.data) {
            if (page === 1) {
              setProducts(response.data);
            } else {
              setProducts((prev) => [...prev, ...response.data]);
            }

            setTotalCount(response.total_count || response.data.length);
            setHasMore(
              response.data.length === perPage &&
                (page * perPage < (response.total_count || 0))
            );
          } else {
            setProducts([]);
            setTotalCount(0);
            setHasMore(false);
          }
        } catch (err) {
          console.error("Error fetching products:", err);
          setError("Kunne ikke laste produkter");
          setProducts([]);
        } finally {
          setIsLoading(false);
        }
      }, 300),
    [
      showInactive,
      showExpired,
      selectedCategory,
      filtersSignature,
      systemProductsOnly,
    ]
  );

  // Fetch products when search query or filters change
  useEffect(() => {
    if (!open) return;

    setCurrentPage(1);
    debouncedSearch(searchQuery, 1);

    return () => {
      debouncedSearch.cancel();
    };
  }, [
    searchQuery,
    showInactive,
    showExpired,
    selectedCategory,
    systemProductsOnly,
    open,
    filtersSignature,
    debouncedSearch,
  ]);

  // Load more products
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      debouncedSearch(searchQuery, nextPage);
    }
  }, [isLoading, hasMore, currentPage, searchQuery, debouncedSearch]);

  // Initialize categories from product-data
  useEffect(() => {
    if (open && categories.length === 0) {
      // Use product type options as categories
      setCategories(productTypeOptions.filter((opt) => opt.value !== 0));
    }
  }, [open, categories.length]);

  // Update quantity for a product
  const updateQuantity = useCallback(
    (productId, quantity) => {
      const newQuantities = new Map(productQuantities);
      const qty = parseFloat(quantity) || 1;
      newQuantities.set(productId, qty);
      setProductQuantities(newQuantities);

      // Update selected products with new quantity
      const selectedFromCurrent = products
        .filter((p) => selectedIds.has(p.id))
        .map((p) => ({
          ...p,
          quantity: newQuantities.get(p.id) || 1,
        }));
      const selectedFromPrevious = selectedProducts
        .filter(
          (p) =>
            selectedIds.has(p.id) &&
            !products.find((prod) => prod.id === p.id)
        )
        .map((p) => ({
          ...p,
          quantity: newQuantities.get(p.id) || p.quantity || 1,
        }));
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }
    },
    [productQuantities, products, selectedIds, selectedProducts, onChange]
  );

  // Toggle product selection
  const toggleProduct = useCallback(
    (product) => {
      const newSelectedIds = new Set(selectedIds);
      const newQuantities = new Map(productQuantities);

      if (newSelectedIds.has(product.id)) {
        newSelectedIds.delete(product.id);
        newQuantities.delete(product.id);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
          newQuantities.clear();
        }
        newSelectedIds.add(product.id);
        // Set default quantity to 1 if not already set
        if (!newQuantities.has(product.id)) {
          newQuantities.set(product.id, 1);
        }
      }

      setSelectedIds(newSelectedIds);
      setProductQuantities(newQuantities);

      // Get selected products - prioritize products from current list (they have full data)
      const selectedFromCurrent = products
        .filter((p) => newSelectedIds.has(p.id))
        .map((p) => ({
          ...p,
          quantity: newQuantities.get(p.id) || 1,
        }));
      const selectedFromPrevious = selectedProducts
        .filter(
          (p) =>
            newSelectedIds.has(p.id) &&
            !products.find((prod) => prod.id === p.id)
        )
        .map((p) => ({
          ...p,
          quantity: newQuantities.get(p.id) || p.quantity || 1,
        }));
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection
      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      productQuantities,
      products,
      selectedProducts,
      multiple,
      onChange,
      onClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setProductQuantities(new Map());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if product is selected
  const isSelected = useCallback(
    (productId) => {
      return selectedIds.has(productId);
    },
    [selectedIds]
  );

  if (!open) return null;

  return (
    <div className="w-full h-full flex flex-col bg-white rounded-none sm:rounded-lg border-0 sm:border border-gray-200 shadow-none sm:shadow-sm min-h-0">
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg produkter" : "Velg produkt"}
            </h3>
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="text-sm text-gray-500 hover:text-gray-700"
                  title="Fjern alle valg"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-1.5 rounded-md transition-colors ${
                  showFilters
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Vis filtre"
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter produkter..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Vis inaktive
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={showExpired}
                    onChange={(e) => setShowExpired(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Vis utgåtte
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={systemProductsOnly}
                    onChange={(e) => setSystemProductsOnly(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t("product.selector.filter_system_products")}
                  </span>
                </label>
              </div>
              {categories.length > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Kategori
                  </label>
                  <select
                    value={selectedCategory || ""}
                    onChange={(e) =>
                      setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)
                    }
                    className="block w-full rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Alle kategorier</option>
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "produkt" : "produkter"} valgt
            </div>
          )}
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-end gap-2 mb-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1.5 rounded-md transition-colors ${
                showFilters
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
              title={t("product.selector.toggle_filters")}
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
          </div>
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Søk etter produkter..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
              <div className="flex flex-wrap gap-2">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Vis inaktive
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={showExpired}
                    onChange={(e) => setShowExpired(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Vis utgåtte
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={systemProductsOnly}
                    onChange={(e) => setSystemProductsOnly(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t("product.selector.filter_system_products")}
                  </span>
                </label>
              </div>
              {categories.length > 0 && (
                <div className="mt-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Kategori
                  </label>
                  <select
                    value={selectedCategory || ""}
                    onChange={(e) =>
                      setSelectedCategory(e.target.value ? parseInt(e.target.value) : null)
                    }
                    className="block w-full rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Alle kategorier</option>
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Product List */}
      <div className="flex-1 overflow-y-auto sm:max-h-[600px]">
        {isLoading && products.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Laster produkter...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Ingen produkter funnet"
                  : "Søk etter produkter for å begynne"}
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {products.slice(0, 20).map((product) => {
              const selected = isSelected(product.id);
              const quantity = productQuantities.get(product.id) || 1;
              return (
                <div
                  key={product.id}
                  className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                    selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                  }`}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 mt-1">
                      <button
                        onClick={() => toggleProduct(product)}
                        className="focus:outline-none"
                      >
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            selected
                              ? "bg-blue-600 border-blue-600"
                              : "border-gray-300 hover:border-blue-400"
                          }`}
                        >
                          {selected && (
                            <CheckIcon className="h-4 w-4 text-white" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Product Image */}
                    {product.main_picture && (
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md overflow-hidden bg-gray-100">
                          <DocumentImage document={product.main_picture} />
                        </div>
                      </div>
                    )}

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {product.displayTitle || product.title}
                          </p>
                          {product.item_number && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Art.nr: {product.item_number}
                            </p>
                          )}
                          {product.sales_price != null && (
                            <p className="text-xs text-gray-600 mt-1">
                              {typeof product.sales_price === 'number' 
                                ? product.sales_price.toFixed(2) 
                                : parseFloat(product.sales_price || 0).toFixed(2)} kr
                            </p>
                          )}
                        </div>

                        {/* Quantity Input */}
                        {selected && (
                          <div className="flex-shrink-0">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Antall
                            </label>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={quantity}
                              onChange={(e) =>
                                updateQuantity(product.id, e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                              onFocus={(e) => e.stopPropagation()}
                              className="w-16 sm:w-20 px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="1"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {hasMore && !isLoading && products.length < 20 && (
          <div className="p-4 text-center border-t border-gray-200">
            <button
              onClick={loadMore}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Last flere produkter
            </button>
          </div>
        )}

        {isLoading && products.length > 0 && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        )}
      </div>

    </div>
  );
}

